/**
 * [디지털 카드 스타일시트 동적 로더]
 *
 * @description
 * DigitalCard 컴포넌트에서 사용하는 CDN CSS를 동적으로 로드합니다.
 * 싱글톤 패턴을 사용하여 중복 로딩을 방지합니다.
 *
 * @pattern
 * usePostCode의 loadPostcode 패턴을 따릅니다.
 *
 * @features
 * - Promise 기반 비동기 로딩
 * - 싱글톤 패턴으로 중복 로딩 방지
 * - 로드 성공/실패 처리
 * - cleanup 지원
 *
 * @usage
 * ```ts
 * // 컴포넌트에서 사용
 * useEffect(() => {
 *   loadDigitalCardStyles()
 *     .then(() => console.log('Styles loaded'))
 *     .catch((error) => console.error('Failed to load styles', error));
 * }, []);
 * ```
 */

const DIGITAL_CARD_STYLES_URL = 'https://cdn.kottonseed.io/assets/fonts/DigitalCardDesign/fonts.css';
const DIGITAL_CARD_STYLES_ID = 'digital_card_styles';

/**
 * 디지털 카드 스타일시트를 동적으로 로드하는 함수
 *
 * @param url - CSS 파일 URL (기본값: DIGITAL_CARD_STYLES_URL)
 * @returns Promise<void> - 로딩 완료 시 resolve, 실패 시 reject
 */
const loadDigitalCardStyles = (function () {
  let promise: Promise<void> | null = null;

  return function (url: string = DIGITAL_CARD_STYLES_URL): Promise<void> {
    // 이미 로딩 중이거나 완료된 경우 캐시된 Promise 반환
    if (promise) return promise;

    promise = new Promise((resolve, reject) => {
      // 이미 DOM에 스타일시트가 있는지 확인
      const existingLink = document.getElementById(DIGITAL_CARD_STYLES_ID);
      if (existingLink) {
        resolve();
        return;
      }

      // <link> 요소 생성
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.id = DIGITAL_CARD_STYLES_ID;
      link.crossOrigin = 'anonymous';

      // 로딩 성공 핸들러
      link.onload = () => {
        resolve();
      };

      // 로딩 실패 핸들러
      link.onerror = (error) => {
        // 실패 시 promise를 null로 초기화하여 재시도 가능하게 함
        promise = null;
        reject(new Error(`Failed to load Digital Card styles from ${url}: ${error}`));
      };

      // DOM에 추가
      document.head.appendChild(link);
    });

    return promise;
  };
})();

/**
 * 로드된 디지털 카드 스타일시트를 제거하는 함수
 *
 * @description
 * cleanup이 필요한 경우 사용합니다.
 * 일반적으로는 한 번 로드된 스타일을 제거할 필요가 없으므로,
 * 특별한 경우에만 사용합니다.
 */
export const removeDigitalCardStyles = () => {
  const link = document.getElementById(DIGITAL_CARD_STYLES_ID);
  if (link) {
    document.head.removeChild(link);
  }
};

export default loadDigitalCardStyles;
